import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { magneticHover } from '../../utils/animationUtils';

gsap.registerPlugin(ScrollTrigger);

const NAV_LINKS = ["Home", "About", "Experience", "Education", "Certifications", "Tools", "Projects", "Services", "Contact"];

const Navbar = ({ onNavigate }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const navRef = useRef(null);
    const containerRef = useRef(null);
    const logoMarkRef = useRef(null);
    const logoTextRef = useRef(null);
    const topLineRef = useRef(null);
    const hireBtnRef = useRef(null);

    const handleNavClick = (targetId) => {
        if (onNavigate) {
            onNavigate(targetId);
        }
        setIsMenuOpen(false);
    };

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Set initial states
            gsap.set(topLineRef.current, { scaleX: 0, transformOrigin: 'left center' });
            gsap.set(navRef.current, { y: -80, opacity: 0 });
            gsap.set(logoMarkRef.current, { scale: 0, rotation: -20 });
            gsap.set(logoTextRef.current, { x: -20, opacity: 0 });
            gsap.set('.nav-item', { y: -20, opacity: 0 });
            gsap.set(hireBtnRef.current, { scale: 0.8, opacity: 0 });

            // Entry timeline
            const tl = gsap.timeline({ delay: 0.1 });
            tl.to(navRef.current, { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' })
              .to(logoMarkRef.current, { scale: 1, rotation: -2, ease: 'elastic.out(1, 0.5)', duration: 0.7 }, '-=0.3')
              .to(logoTextRef.current, { x: 0, opacity: 1, duration: 0.5, ease: 'power3.out' }, '-=0.5')
              .to('.nav-item', { y: 0, opacity: 1, stagger: 0.06, ease: 'power3.out', duration: 0.5 }, '-=0.4')
              .to(hireBtnRef.current, { scale: 1, opacity: 1, ease: 'back.out(1.5)', duration: 0.5 }, '-=0.3');

            // Global scroll progress bar
            gsap.to(topLineRef.current, {
                scaleX: 1,
                ease: 'none',
                scrollTrigger: {
                    trigger: document.body,
                    start: 'top top',
                    end: 'bottom bottom',
                    scrub: 0.3
                }
            });

            // Scroll-based background (responsive heights)
            const isLaptop = window.innerWidth <= 1024 && window.innerWidth > 768;
            const isMobile = window.innerWidth <= 768;
            
            ScrollTrigger.create({
                start: 80,
                end: 99999,
                onUpdate: (self) => {
                    // Change background when not at top
                    if (self.progress > 0) {
                        gsap.to(navRef.current, {
                            height: isMobile ? '56px' : isLaptop ? '56px' : '60px',
                            backdropFilter: 'blur(8px)',
                            backgroundColor: 'rgba(242,239,233,0.95)',
                            borderBottom: '2px solid #111',
                            boxShadow: '0 2px 0 rgba(0,0,0,0.1)',
                            duration: 0.3,
                            overwrite: 'auto'
                        });
                    } else {
                        gsap.to(navRef.current, {
                            height: isMobile ? '64px' : isLaptop ? '56px' : '64px',
                            backdropFilter: 'blur(0px)',
                            backgroundColor: 'transparent',
                            borderBottom: '2px solid transparent',
                            boxShadow: 'none',
                            duration: 0.3,
                            overwrite: 'auto'
                        });
                    }

                    // Smart hide/show
                    if (self.direction === 1 && self.scroll() > 100) {
                        // Scrolling down: hide ONLY navbar
                        gsap.to(navRef.current, { y: -80, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
                    } else if (self.direction === -1) {
                        // Scrolling up: show
                        gsap.to(navRef.current, { y: 0, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
                    }
                }
            });

            // Logo mark hover
            if (logoMarkRef.current) {
                logoMarkRef.current.addEventListener('mouseenter', () => {
                    gsap.to(logoMarkRef.current, { rotation: 0, scale: 1.08, duration: 0.25, ease: 'power2.out' });
                });
                logoMarkRef.current.addEventListener('mouseleave', () => {
                    gsap.to(logoMarkRef.current, { rotation: -2, scale: 1, ease: 'elastic.out(1,0.5)', duration: 0.6 });
                });
            }

            // Active section spy + Nav link underlines
            const links = document.querySelectorAll('.nav-item');
            links.forEach((link, idx) => {
                const line = link.querySelector('.nav-line');
                const sectionId = NAV_LINKS[idx].toLowerCase();
                const sectionEl = document.getElementById(sectionId);
                
                if (line) {
                    gsap.set(line, { scaleX: 0, transformOrigin: 'left center' });
                    
                    if (sectionEl) {
                        ScrollTrigger.create({
                            trigger: sectionEl,
                            start: 'top 40%',
                            end: 'bottom 40%',
                            onToggle: self => {
                                link.dataset.active = self.isActive ? "true" : "false";
                                gsap.to(line, { scaleX: self.isActive ? 1 : 0, duration: 0.3, ease: 'power3.out', overwrite: 'auto' });
                            }
                        });
                    }

                    link.addEventListener('mouseenter', () => {
                        if (link.dataset.active !== "true") {
                            gsap.to(line, { scaleX: 1, duration: 0.3, ease: 'power3.out', overwrite: 'auto' });
                        }
                    });
                    link.addEventListener('mouseleave', () => {
                        if (link.dataset.active !== "true") {
                            gsap.to(line, { scaleX: 0, duration: 0.2, ease: 'power2.in', overwrite: 'auto' });
                        }
                    });
                }
            });

            // HIRE ME brutalist hover
            if (hireBtnRef.current) {
                hireBtnRef.current.addEventListener('mouseenter', () => {
                    gsap.to(hireBtnRef.current, { x: 4, y: 4, duration: 0.15, ease: 'power2.out' });
                    gsap.to(hireBtnRef.current, { backgroundColor: '#fff700', duration: 0.2 });
                });
                hireBtnRef.current.addEventListener('mouseleave', () => {
                    gsap.to(hireBtnRef.current, { x: 0, y: 0, ease: 'elastic.out(1, 0.5)', duration: 0.5 });
                    gsap.to(hireBtnRef.current, { backgroundColor: '#FFE600', duration: 0.2 });
                });
            }

            // Magnetic hover on logo + CTA
            magneticHover('.nav-logo-mark');
            magneticHover('.nav-cta');
        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        <div ref={containerRef}>
            {/* Brutalist Global Scroll Progress */}
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '12px',
                    backgroundColor: '#F2EFE9',
                    borderBottom: '2.5px solid #111',
                    zIndex: 100,
                }}
            >
                <div
                    ref={topLineRef}
                    style={{
                        height: '100%',
                        width: '100%',
                        backgroundColor: '#FFE600',
                        borderRight: '2.5px solid #111',
                        transformOrigin: 'left center',
                    }}
                />
            </div>

            <nav
                ref={navRef}
                className="fixed left-0 w-full z-50 flex items-center justify-between font-jakarta"
                style={{
                    top: '12px',
                    height: '64px',
                    backgroundColor: 'transparent',
                    borderBottom: '2px solid transparent',
                    padding: '0 clamp(20px, 5vw, 48px)',
                }}
            >
                {/* Logo */}
                <div 
                    data-cursor="link" 
                    onClick={() => handleNavClick('home')} 
                    className="cursor-pointer flex items-center gap-3"
                >
                    <div
                        ref={logoMarkRef}
                        className="nav-logo-mark"
                        style={{
                            background: '#FFE600',
                            border: '2.5px solid #111',
                            boxShadow: '3px 3px 0 #111',
                            borderRadius: 6,
                            width: 'clamp(32px, 4vw, 38px)',
                            height: 'clamp(32px, 4vw, 38px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontFamily: "'Space Mono', monospace",
                            fontWeight: 700,
                            fontSize: 'clamp(12px, 1.5vw, 14px)',
                            color: '#111',
                            transform: 'rotate(-2deg)',
                            flexShrink: 0,
                            cursor: 'pointer',
                        }}
                    >
                        AH
                    </div>
                    <span
                        ref={logoTextRef}
                        style={{
                            fontFamily: "'Syne', sans-serif",
                            fontWeight: 800,
                            fontSize: 'clamp(0.9rem, 1.5vw, 1.1rem)',
                            letterSpacing: '-0.02em',
                            color: '#111',
                        }}
                    >
                        Ahmdhzq<span style={{ color: '#FFE600' }}>.</span>
                    </span>
                </div>

                {/* Desktop Nav */}
                <ul className="hidden md:flex items-center" style={{ gap: 'clamp(4px, 1vw, 32px)' }}>
                    {NAV_LINKS.map((link) => (
                        <li key={link} className="nav-item relative cursor-pointer flex justify-center">
                            <div
                                data-cursor="link"
                                onClick={() => handleNavClick(link.toLowerCase())}
                                style={{
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                    color: '#111',
                                    display: 'block',
                                    cursor: 'pointer',
                                    paddingBottom: 4
                                }}
                            >
                                {link}
                            </div>
                            <span
                                className="nav-line"
                                style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    height: '2.5px',
                                    width: '100%',
                                    backgroundColor: '#FFE600',
                                    borderTop: '1px solid #111',
                                    pointerEvents: 'none'
                                }}
                            />
                        </li>
                    ))}
                </ul>

                {/* HIRE ME CTA */}
                <div className="hidden md:block">
                    <button
                        ref={hireBtnRef}
                        className="nav-cta"
                        onClick={() => handleNavClick('contact')}
                        style={{
                            padding: 'clamp(8px, 1vw, 10px) clamp(16px, 2vw, 20px)',
                            backgroundColor: '#FFE600',
                            border: '2.5px solid #111',
                            boxShadow: '4px 4px 0 #111',
                            borderRadius: 8,
                            fontFamily: "'Syne', sans-serif",
                            fontWeight: 800,
                            fontSize: 'clamp(0.7rem, 1vw, 0.8rem)',
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: '#111',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        HIRE ME <span>↗</span>
                    </button>
                </div>

                {/* Mobile hamburger */}
                <div className="md:hidden">
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="text-[#111] focus:outline-none"
                        aria-label="Toggle menu"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2.5}
                                d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16m-7 6h7"} />
                        </svg>
                    </button>
                </div>
            </nav>

            {/* Mobile Menu */}
            <div className={`
                fixed top-0 left-0 w-full h-screen bg-[#F2EFE9] z-40
                flex flex-col items-center justify-center gap-8
                transition-transform duration-300 ease-in-out md:hidden
                border-r-4 border-[#111]
                ${isMenuOpen ? "translate-x-0" : "-translate-x-full"}
            `}>
                {NAV_LINKS.map((link) => (
                    <div
                        key={link}
                        onClick={() => handleNavClick(link.toLowerCase())}
                        style={{
                            fontSize: '2.5rem',
                            fontWeight: 900,
                            textTransform: 'uppercase',
                            letterSpacing: '-0.02em',
                            color: '#111',
                            cursor: 'pointer',
                        }}
                    >
                        {link}
                    </div>
                ))}
                {/* HIRE ME button in mobile menu */}
                <button
                    onClick={() => handleNavClick('contact')}
                    style={{
                        marginTop: '32px',
                        padding: '14px 32px',
                        backgroundColor: '#FFE600',
                        border: '2.5px solid #111',
                        boxShadow: '4px 4px 0 #111',
                        borderRadius: 8,
                        fontFamily: "'Syne', sans-serif",
                        fontWeight: 800,
                        fontSize: '0.9rem',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: '#111',
                        cursor: 'pointer',
                    }}
                >
                    HIRE ME ↗
                </button>
            </div>
        </div>
    );
};

export default Navbar;
